/// <reference lib="dom" />
/**
 * Canvas2D draw helpers for sky decorations: sun, clouds, planet, spaceships.
 * Web-only (.web.ts) — never bundled on native. Read-only, no gameState mutation.
 * @module webSkyDraw
 */

import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import { drawShip, drawFireTrail } from './webShipDraw';
import type { SkyShip } from './webShipDraw';
import { initMoonExplosions, drawCelestials } from './webCelestialDraw';

// ── Sprite references (set once by GameCanvas RAF init) ──────────────────────
let _cloudLargeImg: HTMLImageElement | null = null;
let _cloudMediumImg: HTMLImageElement | null = null;
let _shipHealthyImg: HTMLImageElement | null = null;
let _shipOnFireImg: HTMLImageElement | null = null;
let _asteroidXLImg: HTMLImageElement | null = null;
let _asteroidLGImg: HTMLImageElement | null = null;
let _asteroidMDImg: HTMLImageElement | null = null;
let _asteroidSMImg: HTMLImageElement | null = null;

/**
 * Provide PixelLab sprite images for sky objects. Called once from the RAF
 * loop after assets load.
 *
 * @param cloudLg - Large cloud sprite (48×48).
 * @param cloudMd - Medium cloud sprite (48×48).
 * @param shipH - Healthy ship sprite (48×48).
 * @param shipF - Ship on fire sprite (48×48).
 * @param astXL - Extra-large asteroid sprite (64×64).
 * @param astLG - Large asteroid sprite (48×48).
 * @param astMD - Medium asteroid sprite (32×32).
 * @param astSM - Small asteroid sprite (16×16).
 */
export function setSkySprites(
  cloudLg: HTMLImageElement,
  cloudMd: HTMLImageElement,
  shipH: HTMLImageElement,
  shipF: HTMLImageElement,
  astXL: HTMLImageElement,
  astLG: HTMLImageElement,
  astMD: HTMLImageElement,
  astSM: HTMLImageElement,
): void {
  _cloudLargeImg = cloudLg;
  _cloudMediumImg = cloudMd;
  _shipHealthyImg = shipH;
  _shipOnFireImg = shipF;
  _asteroidXLImg = astXL;
  _asteroidLGImg = astLG;
  _asteroidMDImg = astMD;
  _asteroidSMImg = astSM;
}

// ── Sky object types ─────────────────────────────────────────────────────────

// DECISION: Pre-compute sky positions once at init (deterministic seed = consistent).
let _skyInitialised = false;
let _skyShips: SkyShip[] = [];
let _skyTime = 0;

function initSky(): void {
  if (_skyInitialised) return;
  _skyInitialised = true;

  let seed = 42;
  const rand = (): number => {
    seed = (seed * 16807 + 0) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  const worldW = 7200;

  // DECISION: 10 ships — 4 healthy, 6 burning. Enough for atmosphere without clutter.
  const shipCount = 10;
  for (let i = 0; i < shipCount; i++) {
    const isOnFire = i >= 4;
    // DECISION: Healthy ships get a wider Y spread (5–65px) so they appear
    // at different altitudes. Burning ships stay higher (5–35px) since they
    // fall during rendering.
    const yRange = isOnFire ? 5 + rand() * 30 : 5 + rand() * 60;
    // DECISION: Every 3rd healthy ship is "rising" — ascending from low on
    // screen with a slight upward drift, giving a take-off appearance.
    const isRising = !isOnFire && (i % 3 === 2);
    // DECISION: Each burning ship gets a random fallSpeed (0.5–2.5×) so
    // crashes look varied — some plummet fast, others drift down slowly.
    _skyShips.push({
      x: (i * worldW / shipCount) + rand() * (worldW / shipCount) - 50,
      y: isRising ? 50 + rand() * 20 : yRange,
      size: 7 + rand() * 7,
      speed: 0.1 + rand() * 0.2,
      flip: rand() > 0.5,
      onFire: isOnFire,
      variant: (i % 5) as 0 | 1 | 2 | 3 | 4,
      rising: isRising,
      fallSpeed: isOnFire ? 0.5 + rand() * 2.0 : 1,
    });
  }

  initMoonExplosions(rand);
}

/**
 * Draw distant sky objects: moon and planet. Called between mid and near
 * parallax layers so the near hills overlap these objects.
 *
 * @param ctx - Canvas 2D rendering context.
 * @param camX - Horizontal camera offset in world pixels.
 * @param dt - Frame delta time in seconds.
 */
export function drawSkyBackground(
  ctx: CanvasRenderingContext2D,
  camX: number,
  dt: number,
): void {
  initSky();
  _skyTime += dt;
  drawCelestials(ctx, camX, _skyTime);
}

/**
 * Draw atmospheric sky objects: sun and clouds.
 * Called after the near parallax layer (these sit in front of hills).
 */
export function drawSkyForeground(
  ctx: CanvasRenderingContext2D,
  camX: number,
): void {
  // ── Sun ──
  const sunX = VIEWPORT_WIDTH - 60 - camX * 0.02;
  const sunY = 30;
  const sunR = 18;

  const glow = ctx.createRadialGradient(sunX, sunY, sunR * 0.3, sunX, sunY, sunR * 2.5);
  glow.addColorStop(0, 'rgba(255,240,180,0.5)');
  glow.addColorStop(0.5, 'rgba(255,200,100,0.15)');
  glow.addColorStop(1, 'rgba(255,200,100,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(sunX - sunR * 3, sunY - sunR * 3, sunR * 6, sunR * 6);

  ctx.fillStyle = '#ffe88a';
  ctx.beginPath();
  ctx.arc(sunX, sunY, sunR, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#fff4c0';
  ctx.beginPath();
  ctx.arc(sunX, sunY, sunR * 0.55, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Draw spaceships between mid and near parallax. Ships fade out as they
 * approach the hill line so they never poke through gaps between hills.
 */
export function drawSkyShips(
  ctx: CanvasRenderingContext2D,
  camX: number,
): void {
  initSky();

  // DECISION: Near-parallax hill tops sit around y ≈ 120-140 on screen.
  // Ships fade from full opacity to invisible between FADE_START and FADE_END
  // so they disappear just as they reach the hill crests.
  const FADE_START = 100;
  const FADE_END = 140;

  // DECISION: Ships wrap around a wide virtual band so they keep reappearing.
  const wrapW = VIEWPORT_WIDTH * 3;
  for (const s of _skyShips) {
    const moveDir = s.flip ? -1 : 1;
    const rawX = s.x - camX * 0.04 + _skyTime * s.speed * 25 * moveDir;
    const sx = ((rawX % wrapW) + wrapW) % wrapW - wrapW * 0.25;
    let sy: number;
    // DECISION: Ships fall past the full viewport (below dirt) before recycling.
    // VIEWPORT_HEIGHT + 40 ensures they exit below the ground tiles.
    const BOTTOM = VIEWPORT_HEIGHT + 40;
    const RANGE = BOTTOM + 30;
    if (s.onFire) {
      // DECISION: fallSpeed multiplier (0.5–2.5) varies each ship's descent
      // rate so crashes look dramatic and varied, not uniform.
      const fall = ((s.x * 7.3 + _skyTime * 6 * s.fallSpeed) % RANGE + RANGE) % RANGE;
      sy = -30 + fall + Math.sin(_skyTime * 3 + s.x * 0.5) * 2;
    } else if (s.rising) {
      const rise = ((s.x * 5.1 + _skyTime * 4) % RANGE + RANGE) % RANGE;
      sy = BOTTOM - rise;
    } else {
      sy = s.y + Math.sin(_skyTime * 0.5 + s.x) * 3;
    }

    // DECISION: Fade ships out as they approach the near-parallax hill line.
    // This prevents ships from showing through gaps between rolling hills.
    if (sy > FADE_END) continue; // fully behind hills — skip draw entirely
    let shipAlpha = 1;
    if (sy > FADE_START) {
      shipAlpha = 1 - (sy - FADE_START) / (FADE_END - FADE_START);
    }
    ctx.save();
    ctx.globalAlpha = shipAlpha;

    if (s.onFire) {
      // DECISION: Crashing ships are rotated ~25° nose-down for dramatic
      // crash trajectory. Rotation pivots around the ship's screen position.
      ctx.translate(sx, sy);
      const crashAngle = s.flip ? -0.44 : 0.44;
      ctx.rotate(crashAngle);
      ctx.translate(-sx, -sy);
      if (_shipOnFireImg) {
        const drawSize = s.size * 2;
        ctx.save();
        if (s.flip) { ctx.translate(sx, sy); ctx.scale(-1, 1); ctx.translate(-sx, -sy); }
        // DECISION: Crop left 4px (stray flame pixels) + bottom 8px label.
        const sW = _shipOnFireImg.naturalWidth - 4;
        const sH = _shipOnFireImg.naturalWidth * (40 / 48);
        ctx.drawImage(_shipOnFireImg, 4, 0, sW, sH, sx - drawSize / 2, sy - drawSize / 2, drawSize, drawSize);
        ctx.restore();
      } else {
        drawFireTrail(ctx, s, sx, sy, _skyTime);
        drawShip(ctx, s, sx, sy);
      }
    } else {
      if (_shipHealthyImg) {
        const drawSize = s.size * 2;
        ctx.save();
        if (s.flip) { ctx.translate(sx, sy); ctx.scale(-1, 1); ctx.translate(-sx, -sy); }
        // Crop bottom 8px text label from PixelLab sprite.
        const srcH = _shipHealthyImg.naturalWidth * (40 / 48);
        ctx.drawImage(_shipHealthyImg, 0, 0, _shipHealthyImg.naturalWidth, srcH, sx - drawSize / 2, sy - drawSize / 2, drawSize, drawSize);
        ctx.restore();
      } else {
        drawShip(ctx, s, sx, sy);
      }
    }

    ctx.restore();
  }
}
